import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SplashPage } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomEvent, Map } from "schemas/functions";
import { routes } from "schemas/routes";

import AboutFormLinks from "features/Auth/Onboarding/AboutForm/Links";
import {
  SocialMediaLinks,
  convertOnboardingFormToLink,
} from "features/Auth/Onboarding/AboutForm/helpers";
import { fetcherAuth } from "utils/api";
import { hasSocialMediaProfile } from "utils/influencer";
import { getSource } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import {
  OrganizationLaunchpadProfileDetails,
  OrganizationLaunchpadProfileStatus,
} from "../schema";

const QuestionsAndAnswers = [
  {
    question: "What stage are you at right now?",
    description: (
      <em>(Choose the one that feels closest to where you are today)</em>
    ),
    options: [
      "I'm just getting started — no brand deals yet",
      "I've done a few gifted collaborations",
      "I've landed a couple of small paid deals",
      "I have regular paid partnerships but want to grow further",
    ],
    type: "radio",
    required: true,
  },
  {
    question: "What type of creator are you?",
    description: <em>(So we can personalize your plan accordingly)</em>,
    options: ["UGC", "Influencer", "Other"],
    type: "radio",
    required: true,
  },
  {
    question: "What are you most focused on building right now?",
    options: [
      "A strong content portfolio to attract brands",
      "My social following and engagement",
      "Landing my first few paid brand deals",
      "Turning gifted partnerships into paid collaborations",
      "Negotiating higher rates with brands",
      "Building long-term partnerships",
      "Landing a dream brand deal",
    ],
    type: "radio",
    required: true,
  },
  {
    question: "How does content creation fit into your week right now?",
    options: [
      "It's my full-time thing — I'm all in",
      "I'm juggling it with work or school",
      "I do it a few hours a week on the side",
      "I'm just getting started and figuring out my rhythm",
    ],
    type: "radio",
    required: true,
  },
  {
    question: "What's been your biggest challenge so far?",
    description: <em>(We'll focus extra support here)</em>,
    options: [
      "I don't know how to pitch brands",
      "I struggle to stay consistent",
      "I'm unsure about the quality of my content",
      "I feel unsure or intimidated during brand conversations or negotiations",
    ],
    type: "radio",
    required: true,
  },
  {
    question:
      "What's one specific goal you want to achieve with Bento Launchpad?",
    description: (
      <>
        <br />
        <em>Be clear and realistic — something you can measure or track.</em>
        <br />
        <em>
          (Examples: Land my first paid brand deal, reach 10K followers,
          collaborate with [Brand Name], or earn $500 in one month.)
        </em>
      </>
    ),
    type: "text",
    required: true,
  },
  {
    question: "How close are you to reaching that goal right now?",
    description: (
      <em>
        (Tell us where you're currently at so we can help you move forward.)
      </em>
    ),
    type: "text",
    required: true,
  },
  {
    question: "When would you like us to start sending your automated pitches?",
    options: ["Right away!", "Next week", "After my first group session"],
    type: "radio",
    required: true,
  },
  {
    question: "Anything specific you'd like help with? (optional)",
    type: "text",
    required: false,
  },
];

const LaunchpadIntakeForm = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { currentOrg, profile } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [linksValidation, setLinksValidation] = useState<{
    [key in string]: string | undefined;
  }>({});
  const [links, setLinks] = useState<SocialMediaLinks[]>([]);
  const [onboardingForm, setOnboardingForm] = useState<Map>({});

  const [backgroundDetails, setBackgroundDetails] = useState<
    OrganizationLaunchpadProfileDetails[]
  >([]);
  const question = QuestionsAndAnswers[questionIndex];
  const answer =
    backgroundDetails?.find((item) => item.index === questionIndex)?.answer ||
    "";
  const other = backgroundDetails?.find(
    (item) => item.index === questionIndex,
  )?.other;

  const handleNext = async (e: CustomEvent) => {
    e.preventDefault();
    let completed = false;
    if (questionIndex < QuestionsAndAnswers.length - 1) {
      setQuestionIndex(questionIndex + 1);
    } else {
      completed = true;
      navigate(`/${routes.launchpadSuccess}`);
    }
    await handleSave(completed);
  };

  const handleBack = () => {
    if (questionIndex > 0) {
      setQuestionIndex(questionIndex - 1);
    }
  };

  const fetchProfile = async () => {
    if (!currentOrg) return;
    try {
      const { organizationLaunchpadProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/organization-launchpad-profiles`,
        "GET",
      );
      setBackgroundDetails(
        organizationLaunchpadProfile.backgroundDetails || [],
      );
      if (
        organizationLaunchpadProfile.status ===
        OrganizationLaunchpadProfileStatus.completed
      ) {
        navigate(`/${routes.launchpadSuccess}`);
      }
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleSave = async (completed?: boolean) => {
    if (!currentOrg) return;

    const copy = makeDeepCopy(backgroundDetails);

    try {
      if (questionIndex === 1 && hasSocialMediaProfile(onboardingForm)) {
        await saveOrgProfile();
      }
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/organization-launchpad-profiles`,
        "POST",
        {},
        {
          source: getSource(),
          backgroundDetails: copy,
          status: completed
            ? OrganizationLaunchpadProfileStatus.completed
            : OrganizationLaunchpadProfileStatus.pending,
        },
      );
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const saveOrgProfile = async () => {
    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/onboarding`,
      "PUT",
      {},
      {
        ...onboardingForm,
        source: getSource(),
        page: SplashPage.launchpad,
      },
    );
  };

  const handleChange = (e: CustomEvent, index: number) => {
    const answer = e.target.value;
    const question = QuestionsAndAnswers[index].question;

    setBackgroundDetails((prev) => {
      const updated = [...prev];
      updated[index] = { question, answer, index };
      return updated;
    });
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchProfile();
    } else {
      navigate(`/${routes.launchpad}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const profileHasLink = hasSocialMediaProfile(profile);
  useEffect(() => {
    if (links?.length === 0 && profileHasLink && profile) {
      const links = convertOnboardingFormToLink(profile);
      setLinks(links);
      setOnboardingForm(profile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  return (
    <Box>
      <Typography variant="h4" textAlign="center">
        🪄 Building Your Step-by-Step Personalized Plan{" "}
      </Typography>

      <Box
        sx={{
          maxWidth: 600,
          px: 8,
          py: 4,
          my: 3,
          borderRadius: 4,
          backgroundColor: "white",
        }}
      >
        <form onSubmit={handleNext}>
          <Grid item xs={12}>
            <FormControl required={question?.required} fullWidth>
              <FormLabel>
                <strong>Question {questionIndex + 1}</strong>:{" "}
                {question?.question}
              </FormLabel>
              {question?.description}
              {question?.options && (
                <RadioGroup
                  onChange={(e) => handleChange(e, questionIndex)}
                  name={`question-${questionIndex}`}
                  value={answer}
                >
                  {question?.options.map((option, optionIndex) => (
                    <FormControlLabel
                      key={optionIndex}
                      value={option}
                      control={<Radio />}
                      label={option}
                    />
                  ))}
                </RadioGroup>
              )}

              {question?.type === "radio" && answer === "Other" && (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  sx={{ mt: 2, width: "100%" }}
                  placeholder="Please specify"
                  value={other || ""}
                  onChange={(e) => {
                    e.preventDefault();
                    const updated = [...backgroundDetails];
                    const existingIndex = updated.findIndex(
                      (item) => item.index === questionIndex,
                    );
                    if (existingIndex !== -1) {
                      updated[existingIndex].other = e.target.value;
                    }
                    setBackgroundDetails(updated);
                  }}
                />
              )}

              {question?.type === "text" && (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  sx={{ mt: 2 }}
                  placeholder="Type your answer here"
                  value={answer}
                  onChange={(e) => {
                    e.preventDefault();
                    handleChange(e, questionIndex);
                  }}
                />
              )}
            </FormControl>
          </Grid>

          {questionIndex === 1 && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />

              <AboutFormLinks
                onboardingForm={onboardingForm}
                setOnboardingForm={setOnboardingForm}
                setLinksValidation={setLinksValidation}
                links={links}
                setLinks={setLinks}
                validation={linksValidation}
              />
            </Box>
          )}

          <Grid container justifyContent={"space-between"} sx={{ mt: 3 }}>
            {questionIndex > 0 ? (
              <Button color="secondary" onClick={handleBack}>
                Back
              </Button>
            ) : (
              <Grid item />
            )}
            <Button
              disabled={
                (answer === "" && question?.required) ||
                (!hasSocialMediaProfile(onboardingForm) && questionIndex === 1)
              }
              type="submit"
              variant="contained"
              disableElevation
            >
              Next
            </Button>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};

export default LaunchpadIntakeForm;
